<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { ParticipationState } from '@/store/consultation.interface';
import { Locales } from '@/store/language.interface';
import { useGuidedConsultationProgress } from '@/composables/useGuidedConsultationProgress';

const props = defineProps<{
  participationState: ParticipationState;
  languageEnabled: Locales[];
}>();

const emit = defineEmits<{
  (e: 'navigateToForm', formId: number, sectionOrderNum: number): void;
  (e: 'submit'): void;
}>();

const {
  isCompleted,
  canComplete,
  mandatoryTotal,
  mandatoryAnswered,
  totalVisible,
  totalAnswered,
  mandatoryPct,
  totalPct,
  missingForms,
  firstMissingForm,
  labels,
} = useGuidedConsultationProgress(toRef(props, 'participationState'), toRef(props, 'languageEnabled'));

const isExpanded = ref(false);
const isSubmitConfirmationOpen = ref(false);

const hasUnansweredOptional = computed(() => canComplete.value && totalAnswered.value < totalVisible.value);

const primaryButtonText = computed(() => (canComplete.value ? labels.value.actionText : labels.value.expandText));

function toggleSheet() {
  if (!isCompleted.value) isExpanded.value = !isExpanded.value;
}

function navigateToQuestion(formId: number, sectionOrderNum: number) {
  isExpanded.value = false;
  emit('navigateToForm', formId, sectionOrderNum);
}

function goToFirstMissingQuestion() {
  if (firstMissingForm.value) {
    navigateToQuestion(firstMissingForm.value.id, firstMissingForm.value.sectionOrderNum);
  }
}

function handleAction() {
  if (!canComplete.value) {
    isExpanded.value = true;
    return;
  }

  isExpanded.value = false;
  if (hasUnansweredOptional.value) {
    isSubmitConfirmationOpen.value = true;
    return;
  }

  emit('submit');
}

function closeSubmitConfirmation() {
  isSubmitConfirmationOpen.value = false;
}

function submitWithMissingOptional() {
  isSubmitConfirmationOpen.value = false;
  emit('submit');
}
</script>

<template>
  <div class="qpb-mobile">
    <!-- Backdrop -->
    <div v-if="isExpanded" class="qpb-mobile__backdrop" @click="isExpanded = false" />

    <!-- Bottom sheet -->
    <Transition name="qpb-sheet">
      <div v-if="isExpanded && !isCompleted" class="qpb-mobile__sheet">
        <div class="qpb-mobile__sheet-handle" />

        <!-- Missing mandatory focus -->
        <template v-if="!canComplete && missingForms.length > 0">
          <div class="qpb-mobile__sheet-heading">{{ labels.remainingMandatory }}</div>
          <div class="qpb-mobile__sheet-hint">{{ labels.tapToNavigate }}</div>
          <div class="qpb-mobile__question-list">
            <button
              v-for="(form, index) in missingForms"
              :key="form.id"
              class="qpb-mobile__question-row"
              @click="navigateToQuestion(form.id, form.sectionOrderNum)"
            >
              <span class="qpb-mobile__question-num">{{ index + 1 }}</span>
              <span class="qpb-mobile__question-title">{{ form.title }}</span>
              <span class="material-icons qpb-mobile__question-arrow">chevron_right</span>
            </button>
          </div>
          <button
            class="qpb-btn qpb-btn--primary qpb-btn--full qpb-mobile__sheet-cta"
            @click="goToFirstMissingQuestion"
          >
            {{ labels.goToFirstMissing }}
            <span class="material-icons">arrow_forward</span>
          </button>
        </template>

        <!-- Progress summary (ready to submit or no more missing) -->
        <template v-else>
          <div class="qpb-mobile__sheet-title">{{ labels.panelTitle }}</div>
          <div class="qpb-mobile__sheet-counts">
            {{ mandatoryAnswered }} / {{ mandatoryTotal }} {{ labels.obligatoires.toLowerCase() }} ·
            {{ totalAnswered }} / {{ totalVisible }} {{ labels.auTotal.toLowerCase() }}
          </div>
          <div class="qpb-mobile__sheet-progress">
            <div class="qpb-progress-row">
              <span class="qpb-progress-row__label">{{ labels.obligatoires }}</span>
              <div class="qpb-progress-row__track">
                <div class="qpb-progress-row__fill" :style="{ width: mandatoryPct + '%' }" />
              </div>
              <span class="qpb-progress-row__count">{{ mandatoryAnswered }}/{{ mandatoryTotal }}</span>
            </div>
            <div class="qpb-progress-row">
              <span class="qpb-progress-row__label">{{ labels.auTotal }}</span>
              <div class="qpb-progress-row__track">
                <div class="qpb-progress-row__fill" :style="{ width: totalPct + '%' }" />
              </div>
              <span class="qpb-progress-row__count">{{ totalAnswered }}/{{ totalVisible }}</span>
            </div>
          </div>
          <button class="qpb-btn qpb-btn--primary qpb-btn--full qpb-mobile__sheet-cta" @click="handleAction">
            {{ labels.actionText }}
          </button>
        </template>
      </div>
    </Transition>

    <!-- Fixed bottom bar (always visible) -->
    <div class="qpb-mobile__bar" @click="toggleSheet">
      <span class="qpb-mobile__bar-count">{{ labels.mandatoryCountShort }}</span>
      <span v-if="isCompleted" class="qpb-mobile__bar-submitted">{{ labels.submittedText }}</span>
      <button v-else class="qpb-btn qpb-btn--primary" @click.stop="handleAction">
        {{ primaryButtonText }}
      </button>
    </div>

    <q-dialog v-model="isSubmitConfirmationOpen">
      <q-card class="qpb-mobile__submit-dialog">
        <q-card-section>
          <div class="qpb-mobile__submit-dialog-title">
            {{ labels.submitConfirmationTitle }}
          </div>
          <div class="qpb-mobile__submit-dialog-text">
            {{
              labels.submitConfirmationBodyStart +
              ' ' +
              labels.submitConfirmationBodyCount +
              ' ' +
              labels.submitConfirmationBodyEnd
            }}
          </div>
        </q-card-section>
        <q-card-actions class="qpb-mobile__submit-dialog-actions">
          <button class="qpb-btn qpb-btn--ghost qpb-btn--full" @click="closeSubmitConfirmation">
            {{ labels.submitConfirmationCancel }}
          </button>
          <button class="qpb-btn qpb-btn--primary qpb-btn--full" @click="submitWithMissingOptional">
            {{ labels.submitConfirmationConfirm }}
            <span class="material-icons">send</span>
          </button>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style lang="scss">
.qpb-mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 200;

  &__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }

  &__bar {
    background: $color-neutral-white;
    border-top: 1px solid $color-neutral-grey-300;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-sm $space-md;
    padding-bottom: calc(#{$space-sm} + env(safe-area-inset-bottom, 0px));
    min-height: 56px;
    cursor: pointer;
  }

  &__bar-count {
    flex: 1;
    font-size: 15px;
    font-weight: 600;
    color: $color-neutral-black;
  }

  &__bar-submitted {
    font-size: 14px;
    font-weight: 600;
    color: $color-primary-darken;
  }

  &__sheet {
    background: $color-neutral-white;
    border-radius: 16px 16px 0 0;
    padding: $space-sm $space-md $space-lg;
    padding-bottom: calc(#{$space-lg} + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
    max-height: 75vh;
    overflow-y: auto;
  }

  &__sheet-handle {
    width: 40px;
    height: 4px;
    background: $color-neutral-grey-300;
    border-radius: 2px;
    margin: 0 auto $space-md;
  }

  &__sheet-heading {
    font-size: 17px;
    font-weight: 700;
    color: $color-neutral-black;
    margin-bottom: 4px;
  }

  &__sheet-hint {
    font-size: 13px;
    color: $color-neutral-grey-400-50;
    margin-bottom: $space-sm;
  }

  &__sheet-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $color-neutral-grey-400-50;
    margin-bottom: 4px;
  }

  &__sheet-counts {
    font-size: 17px;
    font-weight: 700;
    color: $color-neutral-black;
    margin-bottom: $space-md;
  }

  &__sheet-progress {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: $space-md;
  }

  &__sheet-cta {
    margin-top: $space-sm;
  }

  &__question-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: $space-sm;
  }

  &__question-row {
    display: flex;
    align-items: center;
    gap: $space-sm;
    background: $color-neutral-grey-100;
    border: 1px solid $color-neutral-grey-300;
    border-radius: 12px;
    padding: $space-sm $space-md;
    cursor: pointer;
    text-align: left;
    font-size: 14px;
    color: $color-neutral-black;
    transition: background 0.15s ease;

    &:hover {
      background: $color-primary-12;
      border-color: $color-primary;
    }
  }

  &__question-num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: $color-orange-12;
    color: $color-orange;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__question-title {
    flex: 1;
    font-size: 14px;
  }

  &__question-arrow {
    font-size: 20px;
    color: $color-neutral-grey-400-50;
    flex-shrink: 0;
  }

  &__submit-dialog {
    border-radius: 20px;
    max-width: 560px;
    width: calc(100vw - #{$space-md * 2});
    padding: $space-sm;
  }

  &__submit-dialog-title {
    font-size: 28px;
    line-height: 1.2;
    font-weight: 700;
    color: $color-neutral-black;
    margin-bottom: $space-xs;
  }

  &__submit-dialog-text {
    font-size: 22px;
    line-height: 1.25;
    color: $color-neutral-black;
  }

  &__submit-dialog-actions {
    display: flex;
    gap: $space-sm;
    padding: 0 $space-sm $space-sm;

    .qpb-btn {
      flex: 1;
    }
  }
}

// ── Sheet slide-up transition ──────────────────────────────────────────────
.qpb-sheet-enter-active,
.qpb-sheet-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.2s ease;
}

.qpb-sheet-enter-from,
.qpb-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
